import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { compact, percent } from "../analytics";
import { StatusStrip } from "../components/StatusStrip";
import { dayLabel } from "../earlyAccess";
import { errorText, unwrap } from "../problem";
import {
  agoText,
  ageText,
  bytes,
  CHANNEL_LABELS,
  EMAIL_LABELS,
  QUEUE_INFO,
  queueState,
  runDetail,
  runState,
  successShare,
  systemStatus,
  WORKER_INFO,
  type SystemHealth,
} from "../system";

const STATE_TEXT = { ok: "OK", warning: "Behind", failing: "Failing" } as const;
const time = (at: string | null) => (at === null ? "—" : new Date(at).toLocaleString());

export function SystemPage() {
  const health = useQuery({
    queryKey: ["system"],
    queryFn: async () => unwrap(await api.GET("/system")),
    refetchInterval: 30_000,
  });
  return (
    <>
      <h1>System</h1>
      {health.isPending ? (
        <p>Loading…</p>
      ) : health.isError ? (
        <p className="error">{errorText(health.error)}</p>
      ) : (
        <SystemReadout health={health.data} />
      )}
    </>
  );
}

function SystemReadout({ health }: { health: SystemHealth }) {
  const now = Date.parse(health.generated_at);
  return (
    <section className="stack">
      <StatusStrip items={systemStatus(health, now)} label="System status" />
      <p className="muted">
        As of {time(health.generated_at)}, refreshed every 30 seconds. Process and HTTP metrics are on the Prometheus
        endpoint (<code>TENTWENTY_METRICS_PORT</code>).
      </p>
      <Readiness readiness={health.readiness} migrations={health.migrations} />
      {health.runs ? (
        <Workers runs={health.runs} analytics={health.analytics} now={now} />
      ) : (
        <Unreadable title="Background runs" />
      )}
      {health.queues ? <Queues queues={health.queues} now={now} /> : <Unreadable title="Queues" />}
      {health.delivery ? <Delivery delivery={health.delivery} /> : <Unreadable title="Push and email delivery" />}
      {health.unavailable.includes("media") ? <Unreadable title="Chat media" /> : <Media media={health.media} />}
      <Database database={health.database} partitions={health.partitions} />
      <Realtime realtime={health.realtime} />
    </section>
  );
}

// A section whose queries failed or timed out (the server log says why).
function Unreadable({ title }: { title: string }) {
  return (
    <section className="stack">
      <h2>{title}</h2>
      <p className="error">Could not be read just now; the server log has the error.</p>
    </section>
  );
}

function Readiness({
  readiness,
  migrations,
}: {
  readiness: SystemHealth["readiness"];
  migrations: SystemHealth["migrations"];
}) {
  return (
    <section className="stack">
      <h2>Readiness and migrations</h2>
      <table>
        <tbody>
          <tr>
            <th scope="row">Readiness</th>
            <td>
              {readiness.status === "ok" ? "Ready" : readiness.status === "degraded" ? "Degraded" : "Not ready"}
              {" · "}database {readiness.database === "ok" ? "OK" : "failing"}, migrations{" "}
              {readiness.migrations === "ok" ? "OK" : "failing"}
            </td>
          </tr>
          {migrations === null ? (
            <tr>
              <th scope="row">Migrations</th>
              <td className="error">Could not be read just now</td>
            </tr>
          ) : (
            <MigrationRows migrations={migrations} />
          )}
        </tbody>
      </table>
    </section>
  );
}

function MigrationRows({ migrations }: { migrations: NonNullable<SystemHealth["migrations"]> }) {
  return (
    <>
      <tr>
        <th scope="row">Latest applied</th>
        <td>
          {migrations.latest ?? "None"}
          {migrations.latest_applied_at && <span className="muted"> on {time(migrations.latest_applied_at)}</span>}
        </td>
      </tr>
      <tr>
        <th scope="row">This build</th>
        <td>
          {compact(migrations.shipped)} migrations, through {migrations.shipped_latest ?? "—"}
        </td>
      </tr>
      <tr>
        <th scope="row">Pending</th>
        <td>{migrations.pending.length ? migrations.pending.join(", ") : "None"}</td>
      </tr>
      {migrations.unknown > 0 && (
        <tr>
          <th scope="row">Newer than this build</th>
          <td>
            {compact(migrations.unknown)} applied by a later release <span className="muted">(fine to serve)</span>
          </td>
        </tr>
      )}
    </>
  );
}

function Workers({
  runs,
  analytics,
  now,
}: {
  runs: NonNullable<SystemHealth["runs"]>;
  analytics: SystemHealth["analytics"];
  now: number;
}) {
  return (
    <section className="stack">
      <h2>Background runs</h2>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Worker</th>
              <th>Status</th>
              <th>Latest run</th>
              <th className="num">Took</th>
              <th>Did</th>
              <th>Last success</th>
              <th>Last failure</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => {
              const info = WORKER_INFO[run.worker];
              const state = runState(run, now);
              return (
                <tr key={run.worker}>
                  <td>
                    {info.label}
                    <br />
                    <span className="muted">{info.schedule}</span>
                  </td>
                  <td className={`status status-${state.state}`}>{state.text}</td>
                  <td title={time(run.finished_at)}>{agoText(run.finished_at, now)}</td>
                  <td className="num">{run.duration_ms === null ? "—" : ageText(run.duration_ms)}</td>
                  <td>{run.result === null ? "—" : runDetail(run.detail)}</td>
                  <td title={time(run.last_ok_at)}>{agoText(run.last_ok_at, now)}</td>
                  <td title={time(run.last_failed_at)}>{agoText(run.last_failed_at, now)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="muted">
        {analytics === null
          ? "The rollup's state could not be read."
          : `Analytics rollup: ${
              analytics.built_through === null
                ? "not built yet"
                : `through ${dayLabel(analytics.built_through)}${analytics.status === "behind" ? " (behind)" : ""}, built ${agoText(analytics.built_at, now)}`
            }. Daily stats are final before ${
              analytics.daily_stats_retained_from === null ? "no day yet" : time(analytics.daily_stats_retained_from)
            }.`}{" "}
        A run another server instance made shows there; failures are in the server log.
      </p>
    </section>
  );
}

function Queues({ queues, now }: { queues: NonNullable<SystemHealth["queues"]>; now: number }) {
  return (
    <section className="stack">
      <h2>Queues</h2>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Queue</th>
              <th className="num">Pending</th>
              <th className="num">Due now</th>
              <th>Oldest due</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {queues.map((queue) => {
              const info = QUEUE_INFO[queue.key];
              const state = queueState(queue, now);
              return (
                <tr key={queue.key}>
                  <td>
                    {info.label}
                    <br />
                    <span className="muted">{info.note}</span>
                  </td>
                  <td className="num">{compact(queue.pending)}</td>
                  <td className="num">{compact(queue.due)}</td>
                  <td title={time(queue.oldest_due_at)}>
                    {queue.due === 0
                      ? "—"
                      : queue.oldest_due_at === null
                        ? "Since a backfill"
                        : agoText(queue.oldest_due_at, now)}
                  </td>
                  <td className={`status status-${state}`}>{STATE_TEXT[state]}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Delivery({ delivery }: { delivery: NonNullable<SystemHealth["delivery"]> }) {
  const range = `${dayLabel(delivery.from)} and ${dayLabel(delivery.to)} (UTC, so far)`;
  return (
    <section className="stack">
      <h2>Push and email delivery</h2>
      <p className="muted">
        {range}. Counted as they happen and written each minute. Invalid means the registration was gone and has been
        cleared; unavailable, that the channel is not configured.
      </p>
      {delivery.pushes.length === 0 ? (
        <p>No pushes sent.</p>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Push channel</th>
                <th className="num">Sent</th>
                <th className="num">Invalid</th>
                <th className="num">Failed</th>
                <th className="num">Unavailable</th>
                <th className="num">Success</th>
              </tr>
            </thead>
            <tbody>
              {delivery.pushes.map(({ channel, ...counts }) => (
                <tr key={channel}>
                  <td>{CHANNEL_LABELS[channel] ?? channel}</td>
                  <td className="num">{compact(counts.sent)}</td>
                  <td className="num">{compact(counts.invalid)}</td>
                  <td className="num">{compact(counts.failed)}</td>
                  <td className="num">{compact(counts.unavailable)}</td>
                  <td className="num">{percent(successShare(counts))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {delivery.emails.length === 0 ? (
        <p>No emails sent.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th className="num">Sent</th>
              <th className="num">Failed</th>
              <th className="num">Success</th>
            </tr>
          </thead>
          <tbody>
            {delivery.emails.map(({ kind, ...counts }) => (
              <tr key={kind}>
                <td>{EMAIL_LABELS[kind] ?? kind}</td>
                <td className="num">{compact(counts.sent)}</td>
                <td className="num">{compact(counts.failed)}</td>
                <td className="num">{percent(successShare(counts))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function Meter({ share }: { share: number }) {
  return (
    <span className="meter" aria-hidden="true">
      <span style={{ width: `${Math.min(1, Math.max(0, share)) * 100}%` }} />
    </span>
  );
}

function Media({ media }: { media: SystemHealth["media"] }) {
  return (
    <section className="stack">
      <h2>Chat media</h2>
      {media === null ? (
        <p className="muted">
          Off: <code>TENTWENTY_MEDIA_DIR</code> is not set.
        </p>
      ) : (
        <table>
          <tbody>
            <tr>
              <th scope="row">Stored</th>
              <td>
                {bytes(media.bytes)} in {compact(media.files)} files, from {compact(media.uploaders)} people
                {media.unsent_files > 0 && (
                  <span className="muted">
                    {" "}
                    ({bytes(media.unsent_bytes)} in {compact(media.unsent_files)} uploads not sent yet)
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <th scope="row">Quota</th>
              <td>
                {bytes(media.quota_bytes)} per person; the largest uploader uses{" "}
                <Meter share={media.largest_uploader_bytes / media.quota_bytes} />{" "}
                {percent(media.largest_uploader_bytes / media.quota_bytes)}; {compact(media.uploaders_near_quota)} at
                80% or more
              </td>
            </tr>
            <tr>
              <th scope="row">Disk</th>
              <td>
                {media.disk === null ? (
                  "Unreadable"
                ) : (
                  <>
                    <Meter share={1 - media.disk.free_bytes / media.disk.total_bytes} /> {bytes(media.disk.free_bytes)}{" "}
                    free of {bytes(media.disk.total_bytes)}
                  </>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </section>
  );
}

function Database({
  database,
  partitions,
}: {
  database: SystemHealth["database"];
  partitions: SystemHealth["partitions"];
}) {
  if (database === null || partitions === null) return <Unreadable title="Database" />;
  return (
    <section className="stack">
      <h2>Database</h2>
      <p>
        {bytes(database.bytes)} in all. Location history: {compact(partitions.count)} daily partitions,{" "}
        {bytes(partitions.bytes)}
        {partitions.oldest && partitions.newest
          ? `, ${dayLabel(partitions.oldest)} to ${dayLabel(partitions.newest)}`
          : ""}
        ; today's {partitions.today ? "is ready" : "is missing"}, tomorrow's{" "}
        {partitions.tomorrow ? "is ready" : "is missing (run db:partitions)"}.
      </p>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Largest tables</th>
              <th className="num">Size</th>
              <th className="num">Rows (estimate)</th>
            </tr>
          </thead>
          <tbody>
            {database.tables.map((table) => (
              <tr key={table.name}>
                <td>
                  <code>{table.name}</code>
                </td>
                <td className="num">{bytes(table.bytes)}</td>
                <td className="num">{table.rows === null ? "—" : compact(table.rows)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <details>
        <summary>Location partitions</summary>
        <table>
          <thead>
            <tr>
              <th>Day (UTC)</th>
              <th className="num">Size</th>
              <th className="num">Fixes (estimate)</th>
            </tr>
          </thead>
          <tbody>
            {partitions.days.map((day) => (
              <tr key={day.name}>
                <td>{day.day ?? <code>{day.name}</code>}</td>
                <td className="num">{bytes(day.bytes)}</td>
                <td className="num">{day.rows === null ? "—" : compact(day.rows)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {partitions.count > partitions.days.length && (
          <p className="muted">
            The newest {partitions.days.length} of {partitions.count}.
          </p>
        )}
      </details>
    </section>
  );
}

function Realtime({ realtime }: { realtime: SystemHealth["realtime"] }) {
  return (
    <section className="stack">
      <h2>WebSockets</h2>
      <p>
        {compact(realtime.connections)} open connections from {compact(realtime.users)} signed-in people, with{" "}
        {compact(realtime.subscriptions)} circle subscriptions, on this server process.
      </p>
    </section>
  );
}
